// BOLUS_NOTES-14 (2026-09-17): the BolusNotes assistant chat panel.
// BOLUS_NOTES-66 (2026-09-19): honour the backend's `unavailable: true`
// contract. `bolusnotes_assistant` answers HTTP 200 with
// { answer, unavailable: true } when the chat PROVIDER is down, because a
// provider outage is not a client error. We render that as a calm notice
// with a Retry, never as a red error — glucose data, charts and alerts are
// untouched by it, and a scary banner in a diabetes app costs trust for no
// reason. Transport failures get the SAME calm treatment: a raw axios
// message or a 300-char response body in the transcript told a parent
// nothing and looked alarming.
// Thin client over the `bolusnotes_assistant` function: sends one message,
// shows the answer plus which tools the assistant used. Sessions persist on
// the server; the current session id is remembered per browser.
import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import './AssistantPanel.css';

interface ToolUse { tool: string; args: Record<string, unknown>; ok: boolean; ms: number }
interface Msg {
  role: 'user' | 'assistant';
  text: string;
  tools?: ToolUse[];
  model?: string;
  pending?: boolean;
  /** Provider outage (or transport failure) — render as a notice, not an answer. */
  unavailable?: boolean;
  /** The question that produced it, so Retry can resend without retyping. */
  retryOf?: string;
}

/** Mirrors ASSISTANT_UNAVAILABLE in jobs/bolusnotes-assistant.js. Used only
 *  when the request never reached the server, so the wording a parent sees
 *  is identical whether the provider or the network is at fault. */
const UNAVAILABLE_FALLBACK =
  'The assistant is temporarily unavailable, so I cannot answer that right now. ' +
  'Your glucose data, charts and alerts are unaffected — this only affects chat. ' +
  'Please try again in a few minutes.';

const SESSION_KEY = 'bolusnotes_assistant_session';
const SUGGESTIONS = [
  'How did Chase do overnight?',
  "What's Brennan's time in range this week?",
  'Any lows in the last 24 hours?',
  'Compare the last 7 days to the 7 before.',
];

async function callAssistant(action: string, payload: Record<string, unknown>) {
  const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_assistant', { action, payload });
  return res.data?.data ?? res.data?.result ?? res.data;
}

const AssistantPanel: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try { return localStorage.getItem(SESSION_KEY); } catch { return null; }
  });
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !sessionId || messages.length) return;
    callAssistant('get_session', { session_id: sessionId })
      .then((r) => {
        const msgs: Msg[] = [];
        for (const m of r?.messages || []) {
          if (m.role === 'user') msgs.push({ role: 'user', text: m.content });
          else if (m.role === 'assistant') msgs.push({ role: 'assistant', text: m.content });
        }
        setMessages(msgs);
      })
      .catch(() => { setSessionId(null); try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } });
  }, [open, sessionId, messages.length]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: q }, { role: 'assistant', text: '', pending: true }]);
    try {
      const r = await callAssistant('ask', {
        message: q, session_id: sessionId || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (r?.session_id && r.session_id !== sessionId) {
        setSessionId(r.session_id);
        try { localStorage.setItem(SESSION_KEY, r.session_id); } catch { /* ignore */ }
      }
      if (r?.unavailable === true) {
        // Provider down. The server still returns 200 and a usable session id,
        // so the conversation stays intact and a retry can continue it.
        setMessages((m) => [...m.slice(0, -1), {
          role: 'assistant', text: r?.answer || UNAVAILABLE_FALLBACK, unavailable: true, retryOf: q,
        }]);
      } else {
        setMessages((m) => [...m.slice(0, -1), { role: 'assistant', text: r?.answer || '(no answer)', tools: r?.tools, model: r?.model }]);
      }
    } catch (e: any) {
      // Never surface the raw body or axios message here — it is operator
      // detail, not parent-facing. Keep it in the console for debugging.
      // eslint-disable-next-line no-console
      console.warn('[assistant] ask failed', e?.response?.status, e?.response?.data ?? e?.message);
      setMessages((m) => [...m.slice(0, -1), {
        role: 'assistant', text: UNAVAILABLE_FALLBACK, unavailable: true, retryOf: q,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    setMessages([]); setSessionId(null);
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  };

  if (!open) return null;
  return (
    <div className="assistant-backdrop" onClick={onClose}>
      <aside className="assistant-panel" onClick={(e) => e.stopPropagation()} aria-label="BolusNotes assistant">
        <header className="assistant-header">
          <h2>Assistant</h2>
          <div>
            <button className="icon-btn" title="New chat" onClick={newChat}>✚</button>
            <button className="icon-btn" title="Close" onClick={onClose}>✕</button>
          </div>
        </header>
        <div className="assistant-messages">
          {messages.length === 0 && (
            <div className="assistant-empty">
              <p>Ask about the boys' glucose, patterns, or entries. The assistant reads the same data as the dashboard and says which window it looked at.</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} className="assistant-suggestion" onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`assistant-msg assistant-${m.role}${m.unavailable ? ' assistant-unavailable' : ''}`}>
              {m.pending ? <span className="assistant-typing">Looking that up…</span> : <div className="assistant-text">{m.text}</div>}
              {m.unavailable && m.retryOf && (
                <button
                  className="assistant-retry"
                  disabled={busy}
                  onClick={() => {
                    // Drop the failed exchange — the user turn above plus this
                    // notice — because send() appends the user turn again.
                    // Slicing to `i` alone would duplicate the question.
                    setMessages((prev) => {
                      const from = i > 0 && prev[i - 1]?.role === 'user' ? i - 1 : i;
                      return prev.slice(0, from);
                    });
                    void send(m.retryOf as string);
                  }}
                >
                  Try again
                </button>
              )}
              {m.tools && m.tools.length > 0 && (
                <div className="assistant-tools">
                  {m.tools.map((t, j) => (
                    <span key={j} className={`assistant-tool ${t.ok ? '' : 'failed'}`} title={JSON.stringify(t.args)}>
                      {t.tool} · {Math.round(t.ms / 100) / 10}s
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <form className="assistant-input" onSubmit={(e) => { e.preventDefault(); void send(input); }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={busy ? 'Thinking…' : 'Ask about glucose, patterns, entries…'}
            disabled={busy}
            autoFocus
          />
          <button type="submit" disabled={busy || !input.trim()}>Send</button>
        </form>
        <p className="assistant-footnote">Not medical advice — follow your care team's plan. Model: per-app setting in the admin console.</p>
      </aside>
    </div>
  );
};

export default AssistantPanel;
